<template>
  <article
    class="relative min-h-40 flex transition duration-200"
    :class="{
      'xl:w-[250px]': variant === 'narrow',
      'w-full xl:w-[730px] xl:h-auto aspect-16/9': variant === 'wide',
      'xl:scale-110': audioIsPlaying,
    }"
    data-component="CardRecommendationQuestion"
  >
    <div class="grid grid-cols-12 xl:grid-cols-1 w-full rounded-[20px] overflow-hidden shadow-lg hover:shadow-none transform transition duration-200">
      <component
        :is="audio ? 'button' : 'div'"
        v-if="image"
        class="group col-span-4 xl:row-span-4 relative xl:h-[300px]"
        :class="[{ 'col-span-full xl:h-full': image && !description && !title }, audio ? 'focus:ring-4' : '']"
        @click="audio ? togglePlayerState?.() : undefined"
      >
        <figure class="w-full h-full">
          <MoleculePicture
            class="block w-full h-full object-cover transform aspect-16/9"
            :class="{ 'transition duration-200 group-hover:scale-110 group-focus:scale-110': audio }"
            :image="{
              _smUrl: image,
              _smWidth: 352,
              _smHeight: 300,
            }"
            :alt="imgAlt"
          />
        </figure>

        <div v-if="audio" class="flex absolute inset-0" :class="{ 'right-[36px] xl:right-0': title && image }">
          <AudioPlayerOverlay :audio-is-playing="audioIsPlaying" :toggle-player-state="togglePlayerState" :audio="audio" />
        </div>
      </component>

      <section v-if="title" class="c-content relative xl:row-span-1 flex flex-1" :class="{ 'col-span-12': !image, 'col-span-8': image }">
        <div class="z-20 bg-white flex flex-1 flex-col justify-start leading-snug md:leading-tight xl:leading-tight p-5" :class="{ 'pl-0 xl:pl-5 xl:pt-0': image }">
          <h3 class="min-h-12 block max-w-[80%] text-lg md:text-xl xl:text-lg font-semibold text-black line-clamp-2 mb-2" :class="{ 'c-content__margin': image }">
            {{ title }}
          </h3>
          <p v-if="description" class="min-h-20 md:mr-4 xl:mr-4 font-normal text-sm text-black line-clamp-4 mb-4">
            {{ description }}
          </p>
          <MoleculeButton v-if="navigateNext" :key="id" size="large" class="!font-normal mt-auto w-24 xl:w-28 align-bottom md:flex z-10" @click="navigateNext">
            {{ $t('recommendation.questionnaire.card-button-text') }}
          </MoleculeButton>
        </div>
      </section>
    </div>
  </article>
</template>

<script setup lang="ts">
import AudioPlayerOverlay from '@/components/Molecule/Card/AudioPlayerOverlay.vue'

defineProps({
  id: { type: String, required: true },
  variant: { type: String, required: false, default: 'narrow' },
  image: { type: String, required: false, default: undefined },
  imgAlt: { type: String, required: false, default: undefined },
  title: { type: String, required: false, default: undefined },
  description: { type: String, required: false, default: undefined },
  audio: { type: Object as PropType<Record<string, any>>, required: false, default: undefined },
  audioIsPlaying: { type: Boolean, required: false, default: undefined },
  togglePlayerState: { type: Function as PropType<() => void>, required: false, default: undefined },
  navigateNext: { type: Function as PropType<(..._args: any[]) => void>, required: false, default: undefined },
})
</script>

<style lang="scss" scoped>
$side-size-svg: 49px;
$side-size-svg-med: 36px;
$side-size-svg-small: 24px;

$side-size-svg-height: 84px;
$side-size-svg-height-med: 62px;
$side-size-svg-height-small: 42px;

.c-content__margin {
  margin-top: inherit;

  @screen xl {
    margin-top: calc(-1 * theme('spacing.7'));
  }
}

.c-content {
  @apply z-10 mt-0 pt-0;
  min-height: 132px;

  @screen xl {
    padding-top: calc($side-size-svg - 1px);
    margin-top: calc(-1 * ($side-size-svg - 1px));
  }

  &::before {
    content: '';
    background-image: url('/images/brand/BrandLyreCardVertical.svg');
    width: $side-size-svg-small;
    height: $side-size-svg-height-small;
    padding-left: calc($side-size-svg-small - 1px);
    margin-left: calc(-1 * ($side-size-svg-small - 1px));
    @apply left-0 right-auto absolute top-0 bg-no-repeat bg-cover;

    @screen sm {
      height: $side-size-svg-height-med;
      width: $side-size-svg-med;
      padding-left: calc($side-size-svg-med - 1px);
      margin-left: calc(-1 * ($side-size-svg-med - 1px));
    }

    @screen xl {
      @apply right-0 left-auto;
      background-image: url('/images/brand/BrandLyreCardHorizontal.svg');
      width: 84px;
      height: $side-size-svg;
    }
  }

  // horizontal card
  &::after {
    content: '';
    width: $side-size-svg-small;
    height: calc(100% - $side-size-svg-height-small);
    margin-left: calc(-1 * ($side-size-svg-small - 1px));
    @apply absolute bottom-0 left-0 bg-white;

    @screen sm {
      height: calc(100% - $side-size-svg-height-med);
      width: $side-size-svg-med;
      margin-left: calc(-1 * ($side-size-svg-med - 1px));
    }

    //block card
    @screen xl {
      @apply top-0 left-0 ml-0;
      width: calc(100% - $side-size-svg-height);
      height: $side-size-svg;
    }
  }
}
</style>
